import React, { useRef, useState, useCallback, useEffect } from 'react'
import TVChartContainer from '../components/tradingview/TVChartContainer'
import ChartControlPanel from '../components/chart-intelligence/ChartControlPanel'
import PredictionPopover from '../components/chart-intelligence/PredictionPopover'
import { publishSidebar, registerSidebarSetters } from '../stores/chartSidebarStore'
import { API_BASE_URL } from '../config'

const DEFAULT_OVERLAY_STATE = {
  selectedAnalysts: [],
  direction: 'all',
  aggregateOnBar: false,
}

export default function TechnicalChartPage() {
  const chartRef = useRef(null)
  const overlayStateRef = useRef(DEFAULT_OVERLAY_STATE)
  const [overlayState, setOverlayState] = useState(DEFAULT_OVERLAY_STATE)
  const refreshTimerRef = useRef(null)
  const [activePrediction, setActivePrediction] = useState(null)
  const [predLoading, setPredLoading] = useState(false)
  const [currentSymbol, setCurrentSymbol] = useState(null)

  const handleOverlayChange = useCallback((next) => {
    setOverlayState(next)
    clearTimeout(refreshTimerRef.current)

    const prev = overlayStateRef.current
    const removingAnalyst = (next.selectedAnalysts?.length ?? 0) < (prev.selectedAnalysts?.length ?? 0)
    const modeChanged = prev.aggregateOnBar !== next.aggregateOnBar || prev.direction !== next.direction

    overlayStateRef.current = next

    if (modeChanged) {
      // Mode changes replace the entire mark set (different IDs, different data).
      // clearAndRefreshMarks can't serve from cache here (new cache key), so a brief
      // server-fetch flash is unavoidable. clearMarks + refreshMarks is sufficient.
      chartRef.current?.clearMarks()
      chartRef.current?.refreshMarks()
    } else if (removingAnalyst) {
      // Analyst removal: wipe TV's cache then refill synchronously from the client-side
      // cache before the pending repaint fires — no visible flash. See traderbro-datafeed.js.
      chartRef.current?.clearAndRefreshMarks()
    } else {
      // Adding analysts — TV marks are additive, plain refresh suffices.
      chartRef.current?.refreshMarks()
    }
  }, [])

  const handleSelectSymbol = useCallback((ticker) => {
    chartRef.current?.setSymbol(ticker)
  }, [])

  // plan 181: mirror analyst selection into the sidebar bus + register the setters
  // the CLI drives, so window.__brochart selectAnalysts() runs the same overlay
  // path (and chart side-effects) the human UI uses.
  useEffect(() => {
    publishSidebar({ analysts: {
      selected: overlayState.selectedAnalysts || [],
      direction: overlayState.direction || 'all',
    } })
  }, [overlayState.selectedAnalysts, overlayState.direction])

  useEffect(() => {
    registerSidebarSetters({
      setAnalystSelection: (slugs) => handleOverlayChange({ ...overlayStateRef.current, selectedAnalysts: slugs }),
      setAnalystDirection: (direction) => handleOverlayChange({ ...overlayStateRef.current, direction }),
    })
  }, [handleOverlayChange])

  const handleMarkClick = useCallback(async (markId) => {
    const match = markId.match(/^pred_(\d+)$/)
    if (!match) return
    setPredLoading(true)
    setActivePrediction(null)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/research/predictions/${match[1]}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setActivePrediction(await res.json())
    } finally {
      setPredLoading(false)
    }
  }, [])

  return (
    <div className="flex h-full overflow-hidden bg-[#131722]">
      <div className="flex-1 h-full min-w-0 relative">
        <TVChartContainer
          ref={chartRef}
          overlayStateRef={overlayStateRef}
          onMarkClick={handleMarkClick}
          onSymbolChange={setCurrentSymbol}
        />
        {(predLoading || activePrediction) && (
          <PredictionPopover
            prediction={activePrediction}
            loading={predLoading}
            onClose={() => { setActivePrediction(null); setPredLoading(false); }}
          />
        )}
      </div>
      <ChartControlPanel
        overlayState={overlayState}
        onOverlayChange={handleOverlayChange}
        onSelectSymbol={handleSelectSymbol}
        currentSymbol={currentSymbol}
        chartRef={chartRef}
      />
    </div>
  )
}
